import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import CategoryType from "../../models/CategoryType";
import {RequestStatus} from "../../utils/Enums";
import {apiClient} from "../../utils/ApiClient";
import {NewCategoryTypeFormProps} from "../../components/categoryTypes/NewCategoryTypeScreen";
import {ModifyCategoryTypeFormProps} from "../../components/categoryTypes/ModifyCategoryTypeScreen";

interface CategoryTypesState {
    categoryTypes: CategoryType[];
    categoryTypesCount: CategoryType[];
    currentCategoryType?: CategoryType | null;
    currentPage: number;
    totalPages: number;
    getCategoryTypesStatus: RequestStatus;
    getCurrentCategoryType: RequestStatus;
    createCategoryTypeStatus: RequestStatus;
    updateCategoryTypeStatus: RequestStatus;
    deleteCategoryTypeStatus: RequestStatus;
    errors: string[],
}

const initialState: CategoryTypesState = {
    categoryTypes: [],
    categoryTypesCount: [],
    currentCategoryType: null,
    currentPage: 0,
    totalPages: 0,
    getCategoryTypesStatus: RequestStatus.Idle,
    getCurrentCategoryType: RequestStatus.Idle,
    createCategoryTypeStatus: RequestStatus.Idle,
    updateCategoryTypeStatus: RequestStatus.Idle,
    deleteCategoryTypeStatus: RequestStatus.Idle,
    errors: [],
};

export const getCategoryTypes = createAsyncThunk(
    'categoryTypes/getCategoryTypes',
    async (_, {rejectWithValue}) => {
        const response = await apiClient.get('category-types');
        return response.data;
    }
);

export const createCategoryType = createAsyncThunk(
    'categoryTypes/createCategoryType',
    async (data: NewCategoryTypeFormProps, {rejectWithValue}) => {
        const response = await apiClient.post('category-types', data);
        return response.data;
    }
);

export const getCurrentCategoryType = createAsyncThunk(
    'categoryTypes/getCurrentCategoryType',
    async ({id}: { id: string | undefined }, {rejectWithValue}) => {
        const response = await apiClient.get(`category-types/${id}`);
        return response.data;
    }
);

export const updateCategoryType = createAsyncThunk(
    'categoryTypes/updateCategoryType',
    async ({id, data}: { id: string | undefined, data: ModifyCategoryTypeFormProps }, {rejectWithValue}) => {
        const response = await apiClient.put(`category-types/${id}`, data);
        return response.data;
    }
);

export const deleteCategoryType = createAsyncThunk(
    'categoryTypes/deleteCategoryType',
    async ({id}: { id: string | undefined }, {rejectWithValue}) => {
        const response = await apiClient.delete(`category-types/${id}`);
        return response.data;
    }
);

export const getCategoryTypesAll = createAsyncThunk(
    'categoryTypes/getCategoryTypesAll',
    async (_, {rejectWithValue}) => {
        const response = await apiClient.get('category-types/all');
        return response.data;
    }
);

export const getCategoryTypesCount = createAsyncThunk<CategoryType[], { type: string; language: string; country: string }>(
    'categoryTypes/getCategoryTypesCount',
    async ({ type, language, country }, { rejectWithValue }) => {
        const response = await apiClient.get(`categories/get-count/${type}?language=${language}&country=${country}`);
        return response.data;
    }
);


const categoryTypesSlice = createSlice({
    name: 'categoryTypes',
    initialState,
    reducers: {
        resetCreateCategoryTypeStatus: (state) => {
            state.createCategoryTypeStatus = RequestStatus.Idle;
            state.currentCategoryType = null;
        },
        resetUpdateCategoryTypeStatus: (state) => {
            state.updateCategoryTypeStatus = RequestStatus.Idle;
            state.currentCategoryType = null;
        },
        resetDeleteCategoryTypeStatus: (state) => {
            state.deleteCategoryTypeStatus = RequestStatus.Idle;
            state.currentCategoryType = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCategoryTypes.pending, (state, action) => {
                state.getCategoryTypesStatus = RequestStatus.Loading;
            })
            .addCase(getCategoryTypes.fulfilled, (state, action) => {
                state.getCategoryTypesStatus = RequestStatus.Success;
                state.categoryTypes = action.payload.data;
                state.currentPage = action.payload.current_page;
                state.totalPages = action.payload.last_page;
            })
            .addCase(getCategoryTypes.rejected, (state, action) => {
                state.getCategoryTypesStatus = RequestStatus.Failed;
            })

            .addCase(createCategoryType.pending, (state, action) => {
                state.createCategoryTypeStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createCategoryType.fulfilled, (state, action) => {
                state.createCategoryTypeStatus = RequestStatus.Success;
            })
            .addCase(createCategoryType.rejected, (state, action) => {
                state.createCategoryTypeStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(updateCategoryType.pending, (state, action) => {
                state.updateCategoryTypeStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateCategoryType.fulfilled, (state, action) => {
                state.updateCategoryTypeStatus = RequestStatus.Success;
            })
            .addCase(updateCategoryType.rejected, (state, action) => {
                state.updateCategoryTypeStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(deleteCategoryType.pending, (state, action) => {
                state.deleteCategoryTypeStatus = RequestStatus.Loading;
            })
            .addCase(deleteCategoryType.fulfilled, (state, action) => {
                state.deleteCategoryTypeStatus = RequestStatus.Success;
            })
            .addCase(deleteCategoryType.rejected, (state, action) => {
                state.deleteCategoryTypeStatus = RequestStatus.Failed;
            })

            .addCase(getCurrentCategoryType.pending, (state, action) => {
                state.getCurrentCategoryType = RequestStatus.Loading;
            })
            .addCase(getCurrentCategoryType.fulfilled, (state, action) => {
                state.getCurrentCategoryType = RequestStatus.Success;
                state.currentCategoryType = action.payload;
            })
            .addCase(getCurrentCategoryType.rejected, (state, action) => {
                state.getCurrentCategoryType = RequestStatus.Failed;
            })

            .addCase(getCategoryTypesAll.pending, (state) => {
                state.getCategoryTypesStatus = RequestStatus.Loading;
            })
            .addCase(getCategoryTypesAll.fulfilled, (state, action) => {
                state.getCategoryTypesStatus = RequestStatus.Success;
                state.categoryTypes = action.payload;
            })
            .addCase(getCategoryTypesAll.rejected, (state, action) => {
                state.getCategoryTypesStatus = RequestStatus.Failed;
                // Handle the error as needed
            })

            .addCase(getCategoryTypesCount.pending, (state) => {
                state.getCategoryTypesStatus = RequestStatus.Loading;
            })
            .addCase(getCategoryTypesCount.fulfilled, (state, action) => {
                state.getCategoryTypesStatus = RequestStatus.Success;
                state.categoryTypesCount = action.payload;
            })
            .addCase(getCategoryTypesCount.rejected, (state, action) => {
                state.getCategoryTypesStatus = RequestStatus.Failed;
                // Handle the error as needed
            });

    },
});

export const {
    resetCreateCategoryTypeStatus,
    resetUpdateCategoryTypeStatus,
    resetDeleteCategoryTypeStatus
} = categoryTypesSlice.actions;
export default categoryTypesSlice.reducer;